import { useState, useMemo, useCallback, useEffect } from 'react';

interface Props<T> {
  data: T[];
}

function useCheckTable<T extends { id: number }>({ data }: Props<T>) {
  const [checkedData, setCheckedData] = useState<Record<string, boolean>>(
    data.reduce((a, b) => ({ ...a, [b.id]: false }), {}),
  );

  const isAll = useMemo(() => {
    const values = Object.values(checkedData);
    if (values.length === 0) return false;

    return values.every((d) => d);
  }, [checkedData]);

  const handleAllCheck = useCallback(
    (checked: boolean) => {
      setCheckedData(data.reduce((a, b) => ({ ...a, [b.id]: checked }), {}));
    },
    [data],
  );

  const handleCheck = useCallback(
    (id: number, checked: boolean) => {
      checkedData[id] = checked;
      setCheckedData({ ...checkedData });
    },
    [checkedData],
  );

  const getCheckedData = useCallback(
    () =>
      Object.keys(checkedData)
        .filter((k) => checkedData[k])
        .map((k) => Number(k)),
    [checkedData],
  );

  useEffect(() => {
    setCheckedData(data.reduce((a, b) => ({ ...a, [b.id]: false }), {}));
  }, [data]);

  return { isAll, handleAllCheck, checkedData, handleCheck, getCheckedData };
}

export default useCheckTable;
